import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  connect() {
    this.boundKeyHandler = this.handleKeydown.bind(this);
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeyHandler);
    this.removeModal();
  }

  open(event) {
    event.preventDefault();
    event.stopPropagation();
    console.log('Modal open triggered');

    const link = event.currentTarget;
    // Derive URL from href, src, or child media for non-anchor triggers
    let url = link.getAttribute && (link.getAttribute("href") || link.getAttribute("src"));
    if (!url && link.querySelector) {
      const media = link.querySelector('img, embed, iframe, source');
      if (media) {
        url = media.getAttribute('src');
      }
    }
    if (!url) {
      console.warn('No URL found for modal content');
      return;
    }

    // Guard against accidental contact show URLs with undefined id
    if (url.startsWith('/home/contacts/') && url.endsWith('undefined')) {
      console.warn('Aborting modal: invalid media URL', url);
      return;
    }

    const fileType = link.dataset.fileType || this.detectFileType(url);
    const title = link.dataset.modalTitle || (fileType === "pdf" ? "Document" : "Image");

    console.log('Modal URL:', url, 'File type:', fileType);

    this.createModal(title);
    this.showModal();
    this.loadContent(url, fileType);
    this.updateDownloadLink(url);
    
    // Ensure close buttons work after modal is created
    this.bindCloseEvents();
  }

  close() {
    console.log('Modal close triggered');
    const modal = document.getElementById("business-card-modal");
    if (!modal) {
      console.log('No modal found to close');
      return;
    }

    modal.classList.remove("modal--visible");

    setTimeout(() => {
      document.body.classList.remove("modal-open");
      document.removeEventListener("keydown", this.boundKeyHandler);
      this.removeModal();
      console.log('Modal closed and removed');
    }, 300);
  }

  closeOnBackdrop(event) {
    if (event.target.classList.contains("modal__backdrop")) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }

  showModal() {
    const modal = document.getElementById("business-card-modal");
    if (!modal) return;

    modal.classList.remove("hidden");
    document.body.classList.add("modal-open");
    document.addEventListener("keydown", this.boundKeyHandler);

    requestAnimationFrame(() => {
      modal.classList.add("modal--visible");
    });
  }

  loadContent(url, fileType) {
    const loader = document.querySelector("#business-card-modal [data-modal-loader]");
    const image = document.querySelector("#business-card-modal [data-modal-image]");
    const iframe = document.querySelector("#business-card-modal [data-modal-iframe]");

    // Show loader and hide content
    if (loader) loader.style.display = "flex";
    if (image) image.style.display = "none";
    if (iframe) iframe.style.display = "none";

    if (fileType === "pdf") {
      this.loadPDF(url);
    } else {
      this.loadImage(url);
    }
  }

  loadImage(url) {
    console.log('Loading image:', url);
    const image = document.querySelector("#business-card-modal [data-modal-image]");
    const loader = document.querySelector("#business-card-modal [data-modal-loader]");

    if (!image) {
      console.log('No image element found');
      return;
    }

    const img = new Image();

    img.onload = () => {
      console.log('Image loaded successfully');
      image.src = url;
      image.style.display = "block";
      if (loader) loader.style.display = "none";
    };

    img.onerror = () => {
      console.log('Image failed to load');
      this.showError("Failed to load image");
    };

    img.src = url;
  }

  loadPDF(url) {
    const iframe = document.querySelector("#business-card-modal [data-modal-iframe]");
    const loader = document.querySelector("#business-card-modal [data-modal-loader]");

    if (!iframe) return;

    iframe.src = url;
    iframe.style.display = "block";

    setTimeout(() => {
      if (loader) loader.style.display = "none";
    }, 1000);
  }

  showError(message) {
    const content = document.querySelector("#business-card-modal [data-modal-content]");
    const loader = document.querySelector("#business-card-modal [data-modal-loader]");

    if (content) {
      content.innerHTML = `
        <div class="modal__error">
          <div class="modal__error-icon">⚠️</div>
          <div class="modal__error-message">${message}</div>
          <button class="modal__error-retry" onclick="location.reload()">
            Try Again
          </button>
        </div>
      `;
    }

    if (loader) loader.style.display = "none";
  }

  updateDownloadLink(url) {
    const downloadLink = document.querySelector("#business-card-modal [data-modal-download]");
    if (downloadLink) {
      downloadLink.href = url;
    }
  }

  bindCloseEvents() {
    const modal = document.getElementById("business-card-modal");
    if (!modal) {
      console.log('No modal found for binding close events');
      return;
    }

    // Bind close button events - both header close and footer close
    const closeButtons = modal.querySelectorAll(".modal__close, .modal__close-btn");
    console.log('Found close buttons:', closeButtons.length);
    closeButtons.forEach(button => {
      button.addEventListener("click", (e) => {
        console.log('Close button clicked');
        e.preventDefault();
        this.close();
      });
    });

    // Bind backdrop click
    const backdrop = modal.querySelector(".modal__backdrop");
    if (backdrop) {
      console.log('Binding backdrop click event');
      backdrop.addEventListener("click", (e) => {
        console.log('Backdrop clicked');
        e.preventDefault();
        this.close();
      });
    }
  }

  detectFileType(url) {
    if (url.includes(".pdf") || url.includes("application/pdf")) {
      return "pdf";
    }
    return "image";
  }

  createModal(title = "Preview") {
    if (document.getElementById("business-card-modal")) return;

    const modalHTML = `
      <div id="business-card-modal" class="modal hidden">
        <div class="modal__backdrop"></div>
        <div class="modal__container">
          <div class="modal__header">
            <h3 class="modal__title">${title}</h3>
            <button class="modal__close" type="button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div data-modal-content class="modal__content">
            <div data-modal-loader class="modal__loader" style="display: flex;">
              <div class="modal__spinner"></div>
              <div class="modal__loader-text">Loading...</div>
            </div>
            <img data-modal-image class="modal__image" style="display: none;" alt="Business Card">
            <iframe data-modal-iframe class="modal__iframe" style="display: none;" title="Business Card PDF"></iframe>
          </div>
          <div class="modal__footer">
            <a data-modal-download href="#" class="modal__download" target="_blank">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7,10 12,15 17,10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download
            </a>
            <button class="modal__close-btn" type="button">Close</button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML("beforeend", modalHTML);
  }

  removeModal() {
    const modal = document.getElementById("business-card-modal");
    if (modal) {
      modal.remove();
    }
  }
}
